import type L from "leaflet";
import type { KeyValue, TileFeature } from "@fortex/api";
import type { CatalogueEntry, CatalogueKind, LegendEntry } from "../catalogue/types";
import type { Feature, FeatureBatch, LatLngTuple, ViewQuery } from "../features/types";

export type FeatureProvider = {
  clustering: "server" | "client" | "none";
  load(view: ViewQuery, signal: AbortSignal): Promise<FeatureBatch>;
  search?(term: string, take: number, signal?: AbortSignal): Promise<Feature[]>;
  subscribe?(listener: () => void): () => void;
};

export type InfoHit = {
  id: string;
  layer: string;
  name: string;
  feature: Feature | null;
};

export type InfoFeature = {
  hit: InfoHit;
  feature: TileFeature;
  editable: boolean;
};

export type InfoProvider = {
  hitTest(latlng: LatLngTuple, zoom: number, signal: AbortSignal): Promise<InfoHit[]>;
  feature(hit: InfoHit, latlng: LatLngTuple, zoom: number, signal: AbortSignal): Promise<InfoFeature>;
  save?(hit: InfoHit, data: KeyValue[]): Promise<InfoFeature>;
};

export type LayerSource = {
  entry: CatalogueEntry;
  kind: CatalogueKind | "geojson" | "leaflet" | "track";
  layer: L.Layer;
  pane: string | null;
  bounds(): L.LatLngBounds | null;
  features: FeatureProvider | null;
  info: InfoProvider | null;
  legend(): LegendEntry[] | null;
  setOpacity?(value: number): void;
  update?(entry: CatalogueEntry): void;
  disabledReason?: string | null;
  playback?: TrackPlayback | null;
  dispose(): void;
};

export type TrackPlaybackState = {
  objectId: number;
  playing: boolean;
  speed: number;
  elapsedMs: number;
  durationMs: number;
  position: LatLngTuple | null;
  vertexCount: number;
};

export type TrackPlayback = {
  state(): TrackPlaybackState;
  play(): void;
  pause(): void;
  seek(elapsedMs: number): void;
  setSpeed(speed: number): void;
  subscribe(listener: (state: TrackPlaybackState) => void): () => void;
};

export type SourceContext = {
  map: L.Map;
  langId: () => number;
  authenticated: () => boolean;
  admin: () => boolean;
  paneFor(entry: CatalogueEntry): string;
  zIndexFor(entry: CatalogueEntry): number;
  emitError(entry: CatalogueEntry, status: number | null, message: string): void;
};

export type SourceFactory = (entry: CatalogueEntry, context: SourceContext) => LayerSource | null;

export type SourceRegistry = {
  register(kind: string, factory: SourceFactory): void;
  create(entry: CatalogueEntry, context: SourceContext): LayerSource | null;
  has(kind: string): boolean;
};

export const zIndexBands = {
  base: 0,
  overlay: 200,
  vector: 400,
  custom: 650,
  track: 700,
  selection: 900,
  draw: 950
} as const;
